import { fmtPrice } from "../lib/format";
import { Rating, VerifiedBadge } from "../components/Rating";
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n, catName, tr } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Star, MapPin, Briefcase } from "lucide-react";
import { useSEO } from "../lib/useSEO";
import { SITE_URL, OG_IMAGE } from "../lib/seo";

export default function ProviderProfilePage() {
  const { id } = useParams();
  const { lang, t } = useI18n();
  const [p, setP] = useState(null);
  useEffect(() => { api.get(`/providers/${id}`).then((r) => setP(r.data)); }, [id]);

  const title = p ? `${p.name} | Refine Touch USA` : "Refine Touch USA";
  const description = p ? (p.bio || "").slice(0, 160) : undefined;
  const canonical = p ? `${SITE_URL}/providers/${p.id}` : undefined;
  const jsonLd = p ? [{
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: p.name,
    description: p.bio,
    image: p.avatar,
    address: p.location,
    aggregateRating: p.review_count > 0 ? { "@type": "AggregateRating", ratingValue: p.rating, reviewCount: p.review_count } : undefined,
  }, {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Providers", item: `${SITE_URL}/providers` },
      { "@type": "ListItem", position: 3, name: p.name, item: canonical },
    ],
  }] : [];
  useSEO({ title, description, canonical, ogImage: p?.avatar || OG_IMAGE, jsonLd });

  if (!p) return <div className="p-10">{t("loading")}</div>;

  return (
    <div>
      {/* Cover banner */}
      {p.cover_image && (
        <div data-testid="provider-cover" className="relative w-full h-[38vh] min-h-[240px] overflow-hidden">
          <img src={p.cover_image} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/10" />
        </div>
      )}

      <div className={`max-w-6xl mx-auto px-6 lg:px-10 py-10 ${p.cover_image ? "-mt-20 relative" : ""}`}>
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <img data-testid="provider-avatar" src={p.avatar} className={`w-40 h-40 rounded-full object-cover ${p.cover_image ? "ring-4 ring-white shadow-xl" : ""}`} alt={p.name} />
              <h1 className={`font-serif text-4xl mt-6 ${p.cover_image ? "text-white drop-shadow-[0_3px_18px_rgba(0,0,0,0.85)]" : ""}`}>{p.name}</h1>
              <div className={`text-sm flex items-center gap-1 mt-2 ${p.cover_image ? "text-white/95 drop-shadow-[0_1px_8px_rgba(0,0,0,0.7)]" : "text-stone-500"}`}><MapPin size={14} />{p.location}</div>
              <div className={`flex items-center gap-2 mt-3 ${p.cover_image ? "text-white" : ""}`}>
                <Rating rating={p.rating} count={p.review_count} size={16} light={!!p.cover_image} />
                <VerifiedBadge />
              </div>
              <div className={`text-sm mt-2 flex items-center gap-1 ${p.cover_image ? "text-white/80" : "text-stone-500"}`}><Briefcase size={14} />{p.completed_jobs} {t("jobs_done")}</div>
              <p className="mt-6 text-stone-700 text-sm leading-relaxed bg-white/95 backdrop-blur-sm rounded-xl p-4">{tr(p, "bio", lang)}</p>
              <div className="flex flex-wrap gap-2 mt-4">
                {p.skills?.map((s) => <span key={s} className="text-xs px-3 py-1 rounded-full bg-white text-stone-700 border border-stone-200">{s}</span>)}
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 pt-6 bg-white/95 backdrop-blur-sm rounded-2xl p-6">
            <h2 className="font-serif text-3xl">{t("services_offered")}</h2>
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              {p.services?.map((s) => (
                <Link key={s.id} to={`/services/${s.id}`} className="service-card bg-white border border-stone-200 rounded-2xl overflow-hidden block">
                  <div className="aspect-[4/3] bg-stone-100"><img src={s.images?.[0]} className="w-full h-full object-cover" alt={tr(s, "title", lang)} /></div>
                  <div className="p-4">
                    <div className="font-serif text-lg line-clamp-2">{tr(s, "title", lang)}</div>
                    <div className="flex justify-between items-center mt-2"><span className="text-xs text-stone-500">{s.duration_minutes} min</span><span className="font-serif">{fmtPrice(s.price)}</span></div>
                  </div>
                </Link>
              ))}
            </div>

            <h2 className="font-serif text-3xl mt-12">{t("customer_reviews")}</h2>
            <div className="mt-4 space-y-4">
              {p.reviews?.length === 0 && <p className="text-stone-500 text-sm">{t("no_reviews")}</p>}
              {p.reviews?.map((r) => (
                <div key={r.id} className="border-t border-stone-200 pt-4">
                  <div className="flex items-center gap-3">
                    {r.customer?.avatar && <img src={r.customer.avatar} className="w-8 h-8 rounded-full object-cover" alt={r.customer.name || ""} />}
                    <div>
                      <div className="font-medium text-sm">{r.customer?.name}</div>
                      <div className="flex items-center gap-1">{[...Array(r.rating)].map((_, i) => <Star key={i} size={12} className="fill-amber-400 text-amber-400" />)}</div>
                    </div>
                  </div>
                  <p className="text-sm text-stone-700 mt-2">{tr(r, "comment", lang)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
